import type { OnboardingStatusDto, OnboardingWizardInput, TourKey } from '@osteopro/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '../../lib/api.js';
import { runAuthed } from '../../lib/auth-store.js';
import { onboardingKeys } from '../../lib/query-keys.js';

/**
 * Mise en route du membre. L'état des étapes est calculé par le serveur depuis les données : il se
 * relit à chaque montage (`staleTime: 0`), parce qu'un patient créé ailleurs fait avancer la liste
 * sans qu'aucune écriture d'ici ne l'invalide.
 */
export function useOnboardingStatus(enabled = true) {
  return useQuery({
    queryKey: onboardingKeys.status(),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<OnboardingStatusDto> =>
      runAuthed(async () => unwrap(await api.api.onboarding.$get())),
  });
}

/** Les écritures rendent l'état complet : on le pose dans le cache plutôt que de le relire. */
function useStatusWriter() {
  const queryClient = useQueryClient();
  return (status: OnboardingStatusDto) => {
    queryClient.setQueryData(onboardingKeys.status(), status);
  };
}

/** Relit l'état après une écriture faite ailleurs (paramètres enregistrés depuis l'assistant). */
export function useRefreshOnboarding() {
  const queryClient = useQueryClient();
  return async () => {
    await refreshOnboarding(queryClient);
  };
}

/** Une écriture de paramètres peut cocher une étape : la barre de l'écran doit se relire. */
export function refreshOnboarding(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: onboardingKeys.all() });
}

export function useRecordWizard() {
  const write = useStatusWriter();
  return useMutation({
    mutationFn: async (input: OnboardingWizardInput): Promise<OnboardingStatusDto> =>
      runAuthed(async () => unwrap(await api.api.onboarding.wizard.$post({ json: input }))),
    onSuccess: write,
  });
}

export function useSetChecklistHidden() {
  const write = useStatusWriter();
  return useMutation({
    mutationFn: async (hidden: boolean): Promise<OnboardingStatusDto> =>
      runAuthed(async () => unwrap(await api.api.onboarding.checklist.$post({ json: { hidden } }))),
    onSuccess: write,
  });
}

/**
 * Visite vue : la route répond 204. Le cache est mis à jour d'emblée, pour qu'une visite passée ne
 * se relance pas avant le retour du serveur.
 */
export function useMarkTourSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tour: TourKey): Promise<void> => {
      await runAuthed(async () => unwrap(await api.api.onboarding.tours.$post({ json: { tour } })));
    },
    onMutate: (tour) => {
      queryClient.setQueryData<OnboardingStatusDto>(onboardingKeys.status(), (current) =>
        current === undefined || current.toursSeen.includes(tour)
          ? current
          : { ...current, toursSeen: [...current.toursSeen, tour] },
      );
    },
  });
}
